<template>
  <!-- eslint-disable vue/attribute-hyphenation -->
  <div
    id="homeWidgetCarousel"
    :class="$style.homeWidgetCarousel"
  >
    <LoadingOverlay
      :showLoadingOverlay="showLoading"
    />
    <vueper-slides
      v-if="!showLoading"
      ref="vueperSlides"
      :arrowsOutside="true"
      :bullets="false"
      :bulletsOutside="false"
      :class="['no-shadow', $style.slideContainer]"
      :disable-arrows-on-edges="false"
      :fixed-height="carouselHeight"
      :gap="1"
      :slideMultiple="numberOfVisibleSlides > 1"
      :touchable="false"
      :visible-slides="numberOfVisibleSlides"
      @ready="setNumberOfVisibleSlides"
    >
      <template #arrow-left>
        <div
          :class="[$style.leftArrow, {[$style.disableArrow]: hoveringCard}]"
        >
          <ArrowLeft
            :class="$style.arrow"
          />
        </div>
      </template>
      <template #arrow-right>
        <div
          :class="[$style.rightArrow, {[$style.disableArrow]: hoveringCard}]"
        >
          <ArrowRight
            :class="$style.arrow"
          />
        </div>
      </template>
      <vueper-slide
        v-for="card in cards"
        :key="card.id"
        :class="$style.slide"
        @mouseleave.native="hoveringCard = false"
        @mouseover.native="hoveringCard = true"
      >
        <template v-slot:content>
          <component
            :is="card.cardComponent"
            :card="card"
            v-on="$listeners"
          />
        </template>
      </vueper-slide>
    </vueper-slides>
  </div>
</template>

<script>
import { VueperSlide, VueperSlides } from 'vueperslides';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import ArrowLeft from 'public/img/growth/arrow-left.svg?inline';
import ArrowRight from 'public/img/growth/arrow-right.svg?inline';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';

const onboardingModule = 'OnboardingModule';

export function itemsPropValidator(value) {
  if (!Array.isArray(value)) {
    return false;
  }

  return true;
}

export default {
  name: 'HomeWidgetCarousel',

  components: {
    ArrowLeft,
    ArrowRight,
    LoadingOverlay,
    VueperSlides,
    VueperSlide,
  },

  props: {
    cardComponent: {
      type: [String, Object],
      required: true,
    },
    carouselHeight: {
      type: String,
      default: '250px',
    },
    items: {
      type: Array,
      required: true,
      validator: itemsPropValidator,
    },
    maximumSlides: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      offsetWidth: 0,
      hoveringCard: false,
      numberOfVisibleSlides: 5,
      resizeHandler: () => {},
      showLoading: false,
    };
  },

  computed: {
    ...mapGetters(onboardingModule, {
      implementationChecklistDismissed: 'getImplementationChecklistDismissed',
    }),

    cards() {
      return this.createCardItems(this.items);
    },
  },

  watch: {
    cards(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setLoadingOverlay();
      }
    },

    implementationChecklistDismissed() {
      setTimeout(() => {
        this.setOffsetWidth();
        this.setNumberOfVisibleSlides();
      }, 10);
    },
  },

  mounted() {
    this.setLoadingOverlay();
    this.resizeHandler = () => {
      this.setOffsetWidth();
      this.setNumberOfVisibleSlides();
    };
    window.addEventListener('resize', this.resizeHandler);
  },

  updated() {
    this.$nextTick(() => {
      this.setOffsetWidth();
      this.setNumberOfVisibleSlides();
    });
  },

  destroyed() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  methods: {
    calculateNumberOfVisibleSlides() {
      const parentWidth = this.getParentWidth();

      if (parentWidth > 1860) return this.maximumSlides;
      if (parentWidth > 1480) return 4;
      if (parentWidth > 1135) return 3;
      if (parentWidth > 770) return 2;
      return 1;
    },

    createCardItems(items) {
      let id = Date.now();
      return items.map((item) => {
        id += 1;
        return {
          id: item.id || id,
          cardComponent: this.cardComponent,
          ...item,
        };
      });
    },

    getParentWidth() {
      // The parent div isn't defined if setNumberOfVisibleSlides is called too early
      // Calling setNumberOfVisibleSlides on @ready solves this but we still want a default
      // if the parent div isn't defined we want to default based on window size
      // this adjustment takes into account the implementation checklist and left nav
      const widthAdjustment = this.implementationChecklistDismissed ? 350 : 650;
      const windowSize = window.innerWidth - widthAdjustment;
      // if parentElement.offsetWidth is undefined use a rough estimate based on window size

      // use || instead of get default to avoid returning 0
      return this.offsetWidth || windowSize;
    },

    setLoadingOverlay() {
      // Displaying a loading overlay to get around this open issue with vueper-slides: https://github.com/antoniandre/vueper-slides/issues/142
      this.showLoading = true;
      setTimeout(() => {
        this.showLoading = false;
      }, 500);
    },

    setNumberOfVisibleSlides() {
      this.numberOfVisibleSlides = this.calculateNumberOfVisibleSlides();
    },

    setOffsetWidth() {
      const carouselDiv = document.getElementById('homeWidgetCarousel');
      this.offsetWidth = get(carouselDiv, 'parentElement.offsetWidth');
    },
  },
};
</script>

<style>
  /* Do not remove. This adds global styles for all carousels
  * @todo Refactor carousels to use a single wrapper componet
  * to standardize implementation and import this CSS there.
  */
  @import '../../../node_modules/vueperslides/dist/vueperslides.css';
</style>

<style module>
@value (jcGrey200) from '~css/brand-colors.css';

.homeWidgetCarousel {
  height: 250px;
}

.slide,
.slideContainer {
  max-height: 250px;
}

.leftArrow,
.rightArrow {
  align-content: center;
  align-items: center;
  background-color: white;
  display: flex;
  height: 250px;
  width: 92px;
}

.leftArrow {
  left: 0;
}

.rightArrow {
  right: 0;
}

.slideContainer .arrow {
  margin: 0 auto;
  overflow: unset;
  padding: 0;
  stroke: none;
}

:global(.vueperslides__arrow) .disableArrow {
  display: none;
}
</style>
